import type { PropsWithChildren } from 'react';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { FORGE_MODULE_ATTACHMENT_ACTION } from '../../ForgeModuleType.ts';
import type {
	AttachmentActionSurface,
	ForgeUIAttachmentActionExtensionDataType,
} from '../../types.ts';

import type { AttachmentActionMetadataDescriptor } from './parseAttachmentActionMetadata.ts';
import {
	AttachmentActionMetadataLoadStateContext,
	type AttachmentActionLoadState,
} from './AttachmentActionLoadState.ts';
import type { AttachmentActionDiscoverySuccessReason } from './AttachmentActionDiscoveryExperienceCoordinator.ts';
import {
	type AttachmentActionDiscoveryExperienceStatus,
	useAttachmentActionDiscoveryExperience,
} from './useAttachmentActionDiscoveryExperience.ts';

const EMPTY_DESCRIPTORS: ReadonlyArray<AttachmentActionMetadataDescriptor> = Object.freeze([]);
const EMPTY_DESCRIPTORS_BY_ATTACHMENT_ID: AttachmentActionDescriptorsByAttachmentId = new Map();
const EMPTY_ATTACHMENT_IDS: ReadonlyArray<string> = Object.freeze([]);

export type AttachmentActionDescriptorsByAttachmentId = ReadonlyMap<
	string,
	ReadonlyArray<AttachmentActionMetadataDescriptor>
>;

export type AttachmentActionEligibilityResult = {
	descriptorsByAttachmentId: AttachmentActionDescriptorsByAttachmentId;
	status: AttachmentActionDiscoveryExperienceStatus;
	successReason?: AttachmentActionDiscoverySuccessReason;
};

type AttachmentActionContentInput = {
	id?: string | null;
	type?: string | null;
	subtype?: string | null;
};

type AttachmentActionSpaceInput = {
	id?: string | null;
	key?: string | null;
};

type AttachmentActionProviderContext = Omit<
	ForgeUIAttachmentActionExtensionDataType,
	'attachment' | 'content' | 'space'
> & {
	content: ForgeUIAttachmentActionExtensionDataType['content'];
	space: {
		id: string;
		key: string;
	};
};

type AttachmentActionDescriptorSelection =
	| {
			descriptors: ReadonlyArray<AttachmentActionMetadataDescriptor>;
			mode: 'unconditional';
	  }
	| {
			descriptorsByAttachmentId: AttachmentActionDescriptorsByAttachmentId;
			mode: 'attachment-specific';
	  };

const createEmptyDescriptorSelection = (
	mode: AttachmentActionDescriptorSelection['mode'],
): AttachmentActionDescriptorSelection =>
	mode === 'attachment-specific'
		? { descriptorsByAttachmentId: EMPTY_DESCRIPTORS_BY_ATTACHMENT_ID, mode }
		: { descriptors: EMPTY_DESCRIPTORS, mode };

type AttachmentActionExtensionContextDescriptorSelection =
	| {
			extensions: ReadonlyArray<AttachmentActionMetadataDescriptor>;
			mode?: 'unconditional';
	  }
	| {
			descriptorsByAttachmentId: AttachmentActionDescriptorsByAttachmentId;
			mode: 'attachment-specific';
	  };

export type AttachmentActionExtensionContextValue = AttachmentActionProviderContext &
	AttachmentActionExtensionContextDescriptorSelection & {
		closePopup: (popupKey: string) => void;
		openPopup: (popupKey: string, dismiss: () => void) => void;
	};

export type AttachmentActionExtensionProviderProps = PropsWithChildren<{
	attachmentIds?: ReadonlyArray<string | null | undefined> | null;
	attachmentActionEligibilityResult?: AttachmentActionEligibilityResult;
	content: AttachmentActionContentInput;
	contentAri?: string | null;
	space: AttachmentActionSpaceInput;
	surface: AttachmentActionSurface;
}>;

type AttachmentActionExtensionDiscoveryCommonProps = {
	onDiscoveryStatusChange: (
		status: AttachmentActionDiscoveryExperienceStatus,
		successReason?: AttachmentActionDiscoverySuccessReason,
	) => void;
	surface: AttachmentActionSurface;
};

export type AttachmentActionExtensionDiscoveryProps =
	AttachmentActionExtensionDiscoveryCommonProps & {
		contentId: string;
		mode?: 'unconditional';
		onDescriptors: (descriptors: ReadonlyArray<AttachmentActionMetadataDescriptor>) => void;
		spaceKey: string;
	};

type AttachmentActionDiscoveryState = {
	contextKey: string | null;
	descriptorSelection: AttachmentActionDescriptorSelection;
	generation: number;
	status: AttachmentActionDiscoveryExperienceStatus;
	successReason?: AttachmentActionDiscoverySuccessReason;
};

const AttachmentActionExtensionContext =
	createContext<AttachmentActionExtensionContextValue | null>(null);

export const useAttachmentActionExtensionContext =
	(): AttachmentActionExtensionContextValue | null => useContext(AttachmentActionExtensionContext);

const isNonEmptyString = (value: unknown): value is string =>
	typeof value === 'string' && value.trim().length > 0;

const isConfluenceAri = (value: unknown): value is string =>
	isNonEmptyString(value) && value.trim().startsWith('ari:cloud:confluence:');

const normalizeAttachmentIds = (
	attachmentIds: AttachmentActionExtensionProviderProps['attachmentIds'],
): ReadonlyArray<string> => {
	if (!attachmentIds) {
		return EMPTY_ATTACHMENT_IDS;
	}

	return Array.from(
		new Set(
			attachmentIds.flatMap((attachmentId) =>
				isNonEmptyString(attachmentId) ? [attachmentId.trim()] : [],
			),
		),
	);
};

const resolveProviderContext = ({
	content,
	space,
	surface,
}: Pick<
	AttachmentActionExtensionProviderProps,
	'content' | 'space' | 'surface'
>): AttachmentActionProviderContext | null => {
	if (
		!isNonEmptyString(content.id) ||
		!isNonEmptyString(content.type) ||
		!isNonEmptyString(space.id) ||
		!isNonEmptyString(space.key) ||
		(surface !== 'attachmentPage' && surface !== 'attachmentMacro')
	) {
		return null;
	}

	return {
		type: FORGE_MODULE_ATTACHMENT_ACTION,
		attachmentActionLocation: 'metadata',
		content: {
			id: content.id,
			type: content.type,
			...(content.subtype !== undefined ? { subtype: content.subtype } : {}),
		},
		space: {
			id: space.id,
			key: space.key,
		},
		surface,
	};
};

const renderDiscoveryErrorFallback = (): null => null;

type AttachmentActionExtensionDiscoveryLoadingProps = AttachmentActionExtensionDiscoveryProps & {
	error?: unknown;
};

const AttachmentActionExtensionDiscoveryUnconditionalLoading = ({
	error,
	onDescriptors,
	onDiscoveryStatusChange,
}: AttachmentActionExtensionDiscoveryLoadingProps): null => {
	useEffect(() => {
		if (error) {
			onDescriptors(EMPTY_DESCRIPTORS);
			onDiscoveryStatusChange('failure');
		}
	}, [error, onDescriptors, onDiscoveryStatusChange]);

	return null;
};

const AttachmentActionExtensionDiscoveryLoading = (
	props: AttachmentActionExtensionDiscoveryLoadingProps,
): React.JSX.Element | null => (
	<AttachmentActionExtensionDiscoveryUnconditionalLoading {...props} />
);

const AttachmentActionExtensionDiscoveryLoader: LoadableType<AttachmentActionExtensionDiscoveryProps> =
	LoadableLazy({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-AttachmentActionExtensionDiscovery" */ './AttachmentActionExtensionDiscovery.tsx'
				)
			).AttachmentActionExtensionDiscovery,
		isReactLoadable: true,
		loading: AttachmentActionExtensionDiscoveryLoading,
	});

const createDiscoveryState = (
	contextKey: string | null,
	generation: number,
	mode: AttachmentActionDescriptorSelection['mode'],
): AttachmentActionDiscoveryState => ({
	contextKey,
	descriptorSelection: createEmptyDescriptorSelection(mode),
	generation,
	status: 'loading',
});

type AttachmentActionExtensionProviderStateProps = PropsWithChildren<{
	attachmentActionEligibilityResult?: AttachmentActionEligibilityResult;
	contextKey: string | null;
	enabled: boolean;
	mode: AttachmentActionDescriptorSelection['mode'];
	providerContext: AttachmentActionProviderContext | null;
	surface: AttachmentActionSurface;
}>;

const AttachmentActionExtensionProviderState = ({
	attachmentActionEligibilityResult,
	children,
	contextKey,
	enabled,
	mode,
	providerContext,
	surface,
}: AttachmentActionExtensionProviderStateProps): React.JSX.Element => {
	const participantId = useId();
	const activeContextKey = enabled ? contextKey : null;
	const [discoveryState, setDiscoveryState] = useState<AttachmentActionDiscoveryState>(() =>
		createDiscoveryState(null, 0, mode),
	);
	const activePopupRef = useRef<{ dismiss: () => void; popupKey: string } | null>(null);
	const closeActivePopup = useCallback(() => {
		const activePopup = activePopupRef.current;
		activePopupRef.current = null;
		activePopup?.dismiss();
	}, []);

	useEffect(() => {
		closeActivePopup();
		setDiscoveryState((currentState) => {
			if (currentState.contextKey === activeContextKey) {
				return currentState;
			}

			return createDiscoveryState(activeContextKey, currentState.generation + 1, mode);
		});

		return closeActivePopup;
	}, [activeContextKey, closeActivePopup, mode]);

	const openPopup = useCallback((popupKey: string, dismiss: () => void) => {
		const activePopup = activePopupRef.current;
		if (activePopup?.popupKey !== popupKey) {
			activePopup?.dismiss();
		}
		activePopupRef.current = { dismiss, popupKey };
	}, []);
	const closePopup = useCallback((popupKey: string) => {
		if (activePopupRef.current?.popupKey === popupKey) {
			activePopupRef.current = null;
		}
	}, []);

	const isCurrentGeneration = discoveryState.contextKey === activeContextKey;
	const isExternalAttachmentSpecificDiscovery =
		mode === 'attachment-specific' && attachmentActionEligibilityResult !== undefined;
	const experienceStatus = isExternalAttachmentSpecificDiscovery
		? attachmentActionEligibilityResult.status
		: isCurrentGeneration
			? discoveryState.status
			: 'loading';
	useAttachmentActionDiscoveryExperience({
		discoveryKey: activeContextKey,
		enabled: enabled && activeContextKey !== null,
		participantId,
		status: experienceStatus,
		...(experienceStatus === 'success' &&
		(isExternalAttachmentSpecificDiscovery
			? attachmentActionEligibilityResult.successReason
			: discoveryState.successReason)
			? {
					successReason: isExternalAttachmentSpecificDiscovery
						? attachmentActionEligibilityResult.successReason
						: discoveryState.successReason,
				}
			: {}),
		surface,
	});

	const { generation } = discoveryState;
	const handleDescriptors = useCallback(
		(descriptors: ReadonlyArray<AttachmentActionMetadataDescriptor>) => {
			setDiscoveryState((currentState) => {
				if (
					currentState.contextKey !== activeContextKey ||
					currentState.generation !== generation ||
					currentState.descriptorSelection.mode !== 'unconditional' ||
					currentState.descriptorSelection.descriptors === descriptors
				) {
					return currentState;
				}

				return {
					...currentState,
					descriptorSelection: { descriptors, mode: 'unconditional' },
				};
			});
		},
		[activeContextKey, generation],
	);
	const handleDiscoveryStatusChange = useCallback<
		AttachmentActionExtensionDiscoveryProps['onDiscoveryStatusChange']
	>(
		(status, successReason) => {
			setDiscoveryState((currentState) => {
				if (
					currentState.contextKey !== activeContextKey ||
					currentState.generation !== generation
				) {
					return currentState;
				}

				const nextSuccessReason = status === 'success' ? successReason : undefined;
				const shouldClearDescriptors = status !== 'success';
				const descriptorsAreAlreadyEmpty =
					currentState.descriptorSelection.mode === 'attachment-specific'
						? currentState.descriptorSelection.descriptorsByAttachmentId ===
							EMPTY_DESCRIPTORS_BY_ATTACHMENT_ID
						: currentState.descriptorSelection.descriptors === EMPTY_DESCRIPTORS;
				if (
					currentState.status === status &&
					currentState.successReason === nextSuccessReason &&
					(!shouldClearDescriptors || descriptorsAreAlreadyEmpty)
				) {
					return currentState;
				}

				const { successReason: _currentSuccessReason, ...stateWithoutSuccessReason } = currentState;
				return {
					...stateWithoutSuccessReason,
					descriptorSelection: shouldClearDescriptors
						? createEmptyDescriptorSelection(currentState.descriptorSelection.mode)
						: currentState.descriptorSelection,
					status,
					...(nextSuccessReason ? { successReason: nextSuccessReason } : {}),
				};
			});
		},
		[activeContextKey, generation],
	);
	const handleDiscoveryFailure = useCallback(() => {
		handleDiscoveryStatusChange('failure');
	}, [handleDiscoveryStatusChange]);

	const activeDescriptorSelection = useMemo<AttachmentActionDescriptorSelection>(() => {
		if (isExternalAttachmentSpecificDiscovery) {
			return attachmentActionEligibilityResult.status === 'success'
				? {
						descriptorsByAttachmentId: attachmentActionEligibilityResult.descriptorsByAttachmentId,
						mode: 'attachment-specific',
					}
				: createEmptyDescriptorSelection(mode);
		}

		return enabled && isCurrentGeneration && discoveryState.status === 'success'
			? discoveryState.descriptorSelection
			: createEmptyDescriptorSelection(mode);
	}, [
		attachmentActionEligibilityResult,
		discoveryState.descriptorSelection,
		discoveryState.status,
		enabled,
		isCurrentGeneration,
		isExternalAttachmentSpecificDiscovery,
		mode,
	]);
	const hasActiveDescriptors =
		activeDescriptorSelection.mode === 'attachment-specific'
			? Array.from(activeDescriptorSelection.descriptorsByAttachmentId.values()).some(
					(descriptors) => descriptors.length > 0,
				)
			: activeDescriptorSelection.descriptors.length > 0;
	const loadState = useMemo<AttachmentActionLoadState>(
		() => ({
			status: enabled ? experienceStatus : 'disabled',
			hasExtensions: enabled && hasActiveDescriptors,
		}),
		[enabled, experienceStatus, hasActiveDescriptors],
	);
	const contextValue = useMemo<AttachmentActionExtensionContextValue | null>(
		() =>
			enabled && providerContext && hasActiveDescriptors
				? {
						...providerContext,
						closePopup,
						openPopup,
						...(activeDescriptorSelection.mode === 'attachment-specific'
							? {
									descriptorsByAttachmentId: activeDescriptorSelection.descriptorsByAttachmentId,
									mode: 'attachment-specific' as const,
								}
							: {
									extensions: activeDescriptorSelection.descriptors,
									mode: 'unconditional' as const,
								}),
					}
				: null,
		[
			activeDescriptorSelection,
			closePopup,
			enabled,
			hasActiveDescriptors,
			openPopup,
			providerContext,
		],
	);

	return (
		<AttachmentActionExtensionContext.Provider value={contextValue}>
			<AttachmentActionMetadataLoadStateContext.Provider value={loadState}>
				{children}
			</AttachmentActionMetadataLoadStateContext.Provider>
			{enabled &&
			providerContext &&
			activeContextKey &&
			isCurrentGeneration &&
			mode === 'unconditional' ? (
				<GenericErrorBoundary
					attribution={Attribution.PAGE_EXTENSIONS}
					key={`${activeContextKey}:${generation}`}
					onError={handleDiscoveryFailure}
					renderOnError={renderDiscoveryErrorFallback}
				>
					{mode === 'unconditional' ? (
						<AttachmentActionExtensionDiscoveryLoader
							contentId={providerContext.content.id}
							key={`${activeContextKey}:${generation}`}
							mode="unconditional"
							onDescriptors={handleDescriptors}
							onDiscoveryStatusChange={handleDiscoveryStatusChange}
							spaceKey={providerContext.space.key}
							surface={providerContext.surface}
						/>
					) : null}
				</GenericErrorBoundary>
			) : null}
		</AttachmentActionExtensionContext.Provider>
	);
};

export const AttachmentActionExtensionProvider = ({
	attachmentIds,
	attachmentActionEligibilityResult,
	children,
	content,
	contentAri,
	space,
	surface,
}: AttachmentActionExtensionProviderProps): React.JSX.Element => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { id: contentId, subtype: contentSubtype, type: contentType } = content;
	const { id: spaceId, key: spaceKey } = space;
	const providerContext = useMemo(
		() =>
			resolveProviderContext({
				content: {
					id: contentId,
					subtype: contentSubtype,
					type: contentType,
				},
				space: {
					id: spaceId,
					key: spaceKey,
				},
				surface,
			}),
		[contentId, contentSubtype, contentType, spaceId, spaceKey, surface],
	);
	const normalizedAttachmentIds = useMemo(
		() => normalizeAttachmentIds(attachmentIds),
		[attachmentIds],
	);
	const normalizedContentAri = isConfluenceAri(contentAri) ? contentAri.trim() : null;
	const unconditionalContextKey = useMemo(
		() =>
			providerContext
				? JSON.stringify([
						'unconditional',
						providerContext.surface,
						providerContext.content.id,
						providerContext.space.id,
						providerContext.space.key,
					])
				: null,
		[providerContext],
	);
	const attachmentSpecificContextKey = useMemo(
		() =>
			providerContext && normalizedContentAri && normalizedAttachmentIds.length > 0
				? JSON.stringify([
						'attachment-specific',
						providerContext.surface,
						normalizedContentAri,
						...normalizedAttachmentIds,
					])
				: null,
		[normalizedAttachmentIds, normalizedContentAri, providerContext],
	);

	if (
		renderServerPlaceholder ||
		providerContext === null ||
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		!fg('forge_attachment_action_module_metadata')
	) {
		return (
			<AttachmentActionExtensionProviderState
				attachmentActionEligibilityResult={undefined}
				contextKey={null}
				enabled={false}
				mode="unconditional"
				providerContext={providerContext}
				surface={surface}
			>
				{children}
			</AttachmentActionExtensionProviderState>
		);
	}

	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (fg('forge_attachment_display_conditions')) {
		return (
			<AttachmentActionExtensionProviderState
				attachmentActionEligibilityResult={attachmentActionEligibilityResult}
				contextKey={attachmentSpecificContextKey}
				enabled={attachmentSpecificContextKey !== null}
				mode="attachment-specific"
				providerContext={providerContext}
				surface={surface}
			>
				{children}
			</AttachmentActionExtensionProviderState>
		);
	}

	return (
		<AttachmentActionExtensionProviderState
			attachmentActionEligibilityResult={undefined}
			contextKey={unconditionalContextKey}
			enabled
			mode="unconditional"
			providerContext={providerContext}
			surface={surface}
		>
			{children}
		</AttachmentActionExtensionProviderState>
	);
};
